"use client";

// Ported from relay-protocol packages/relay-widget/src/components/StatusTracker.tsx
// (v0.1.0). Mechanical substitution only: CSS Modules `styles.x` references
// become literal "rd-x" class names (see ../styles/component.css's header
// mapping table), the CSS Modules import becomes the plain side-effect import
// below, and the wire types now come from ../lib/types. Everything else --
// props, layout, and behavior -- is the original logic, untouched.
//
// The reference component polls GET /v1/orders/{id} itself via react-query;
// this component is purely presentational and expects a caller to own that
// poll (no react-query, per the package's peer-deps-only constraint) and
// pass the latest result in as `order`/`error`. Not wired into
// <RelayDeposit>'s default address-first flow (Task 4 uses OrdersList's
// GET /v1/orders?user= list instead, since a reusable address has no single
// "the order" to track) -- kept as an export for hosts that want a single
// order's deposit -> bridge -> deliver stepper, e.g. for one row picked out
// of that list.
import type { Order, StepStatus } from "../lib/types";
import "../styles/component.css";

export interface StatusTrackerProps {
  orderId: string;
  /** Latest polled order (steps/txs) from GET /v1/orders/{orderId}, owned by the caller. */
  order?: Order;
  /** Latest poll error, owned by the caller. */
  error?: Error;
}

/** tx-hash link targets by GET /v1/orders/{id}'s txs.* keys. Best-effort --
 * bscBridge is always a BSC tx, crossFinalize/crossExecute always CROSS. */
const EXPLORER_BASE_URL: Record<keyof Order["txs"], string> = {
  bscBridge: "https://bscscan.com/tx/",
  crossFinalize: "https://scan.crosstoken.io/tx/",
  crossExecute: "https://scan.crosstoken.io/tx/",
};

const FRIENDLY_TX_LABEL: Record<keyof Order["txs"], string> = {
  bscBridge: "Bridge tx (BSC)",
  crossFinalize: "Finalize tx (CROSS)",
  crossExecute: "Execute tx (CROSS)",
};

function stepGlyph(status: StepStatus): string {
  if (status === "done") return "✓";
  if (status === "active") return "●";
  return "";
}

function isTerminalFailure(status: string): boolean {
  return status === "FAILED" || status === "REFUNDED" || status === "RELAY_REFUNDED" || status === "EXPIRED";
}

function stepperItemClassName(status: StepStatus): string {
  if (status === "done") return "rd-stepper-item rd-stepper-item-done";
  if (status === "active") return "rd-stepper-item rd-stepper-item-active";
  return "rd-stepper-item rd-stepper-item-pending";
}

export function StatusTracker({ orderId, order, error }: StatusTrackerProps) {
  // No first poll response yet (and no error either) -- mirrors the
  // reference component's react-query `isLoading`.
  const isLoading = !order && !error;

  return (
    <section className="rd-card">
      <div className="rd-card-header">
        <h2 className="rd-card-title">4. Status</h2>
        {order && (
          <span
            className={`rd-status-pill${
              order.status === "COMPLETED"
                ? " rd-status-pill-success"
                : isTerminalFailure(order.status)
                  ? " rd-status-pill-failure"
                  : ""
            }`}
          >
            {order.status}
          </span>
        )}
      </div>

      <p className="rd-card-hint">Order {orderId}</p>

      {isLoading && (
        <p className="rd-muted">
          <span className="rd-spinner" /> Loading status...
        </p>
      )}

      {error && (
        <div className="rd-callout rd-callout-danger">
          Couldn&apos;t load order status: {error.message}
        </div>
      )}

      {order && (
        <>
          <ol className="rd-stepper">
            {order.steps.map((step, i) => (
              <li key={step.key} className={stepperItemClassName(step.status)}>
                <div className="rd-stepper-rail">
                  <span className="rd-stepper-dot">{stepGlyph(step.status)}</span>
                  {i < order.steps.length - 1 && <span className="rd-stepper-line" />}
                </div>
                <div className="rd-stepper-body">
                  <div className="rd-stepper-label">{step.label}</div>
                  {step.key === "bridge" && order.txs.bscBridge && (
                    <div className="rd-stepper-meta">
                      <TxLink orderTxs={order.txs} txKey="bscBridge" />
                    </div>
                  )}
                  {step.key === "deliver" && (order.txs.crossFinalize || order.txs.crossExecute) && (
                    <div className="rd-stepper-meta">
                      {order.txs.crossFinalize && <TxLink orderTxs={order.txs} txKey="crossFinalize" />}
                      {order.txs.crossExecute && <TxLink orderTxs={order.txs} txKey="crossExecute" />}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>

          {isTerminalFailure(order.status) && (
            <div className="rd-callout rd-callout-danger">
              This order did not complete successfully (status: {order.status}). If funds were deposited, refer to
              the refund address on the order for recovery.
            </div>
          )}

          {order.status === "COMPLETED" && (
            <div className="rd-callout rd-callout-success">crossd delivered. This order is complete.</div>
          )}
        </>
      )}
    </section>
  );
}

function TxLink({ orderTxs, txKey }: { orderTxs: Order["txs"]; txKey: keyof Order["txs"] }) {
  const hash = orderTxs[txKey];
  if (!hash) return null;
  return (
    <a href={`${EXPLORER_BASE_URL[txKey]}${hash}`} target="_blank" rel="noreferrer">
      {FRIENDLY_TX_LABEL[txKey]}: {hash.slice(0, 10)}...
    </a>
  );
}
